/**
 * t269u: transport retries and friendly error text for the shared API client.
 *
 * A deploy recreates the API container for a few seconds; Caddy answers 502/503 (or 504) meanwhile. The client retries
 * those with backoff for about 15–20 s, but only when a repeat can't do the work twice:
 * - GET / HEAD / OPTIONS / PUT (idempotent by meaning) retry on 502, 503, 504 and network errors.
 * - POST / PATCH / DELETE retry on 502 and 503 (the proxy answered: the API never ran it). 504 and network errors
 *   (the request may have reached the API) only retry when the request carries an Idempotency-Key the API honours,
 *   or is idempotent by its own id (chat messages by client_message_id).
 * - A 409 `idempotency_in_progress` (the first try is still running) is retried for keyed requests.
 * - 4xx and plain 500 are never retried. Offline: no retries (the outboxes queue and resend on reconnect).
 */

/** Waits before the 2nd, 3rd … try: 0.5 s, 1 s, 2 s, 4 s, 4 s, 4 s (±20 % jitter), about 15.5 s in all. */
export const RETRY_DELAYS_MS = [500, 1000, 2000, 4000, 4000, 4000] as const;
/** No retry starts after this long since the first try. */
export const RETRY_BUDGET_MS = 20_000;
/** A Retry-After longer than this is capped (it still counts against the budget). */
export const RETRY_AFTER_CAP_MS = 8_000;

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT']);
export const isIdempotentMethod = (method: string) => IDEMPOTENT_METHODS.has(method.toUpperCase());

export interface RetryCheck {
  method: string;
  /** HTTP status, or null for a network error (fetch threw). */
  status: number | null;
  /** The request is safe to repeat: an Idempotency-Key the route honours, or idempotent by its own id. */
  safeToRepeat?: boolean;
  /** `error` code from the JSON body (e.g. idempotency_in_progress). */
  code?: string | null;
  offline?: boolean;
}

/** Whether this failure may be tried again (the attempt count and time budget are checked separately). */
export function isRetryable({ method, status, safeToRepeat = false, code, offline = false }: RetryCheck): boolean {
  if (offline) return false;
  const repeatable = isIdempotentMethod(method) || safeToRepeat;
  if (status === null) return repeatable;
  if (status === 502 || status === 503) return true;
  if (status === 504) return repeatable;
  if (status === 409 && code === 'idempotency_in_progress') return safeToRepeat;
  return false;
}

/** Retry-After as milliseconds: delta seconds or an HTTP date. Null when absent or unreadable. */
export function parseRetryAfter(value: string | null | undefined, now = Date.now()): number | null {
  if (!value) return null;
  const v = value.trim();
  if (/^\d+(\.\d+)?$/.test(v)) return Math.round(Number(v) * 1000);
  const at = Date.parse(v);
  return Number.isNaN(at) ? null : Math.max(0, at - now);
}

/**
 * How long to wait before retry number `retry` (0 = the first retry), or null when there are no retries left or the
 * wait would run past the budget. Retry-After wins when it asks for longer (capped).
 */
export function retryDelay(retry: number, opts: { retryAfterMs?: number | null; elapsedMs?: number; random?: () => number } = {}): number | null {
  if (retry < 0 || retry >= RETRY_DELAYS_MS.length) return null;
  const rnd = opts.random ?? Math.random;
  const base = RETRY_DELAYS_MS[retry] * (0.8 + 0.4 * rnd());
  const wait = Math.round(Math.max(base, Math.min(opts.retryAfterMs ?? 0, RETRY_AFTER_CAP_MS)));
  if ((opts.elapsedMs ?? 0) + wait > RETRY_BUDGET_MS) return null;
  return wait;
}

/** Plain en-GB text for an API failure. Raw status codes and server internals never reach people. */
export const FRIENDLY = {
  updating: 'The Hub is updating. Please try again in a moment.',
  offline: 'You’re offline. Check your connection and try again.',
  server: 'Something went wrong on our side. Please try again.',
  expired: 'Your session has expired. Please sign in again.',
  forbidden: 'You don’t have permission to do that.',
  notFound: 'That item no longer exists.',
  tooBig: 'That file is too big.',
  tooMany: 'Too many attempts. Please wait a moment.',
  generic: 'That didn’t work. Please try again.',
} as const;

/**
 * The message for a failed request. `serverMessage` is the API's own `message` (already joined); it is kept for 4xx
 * answers that explain what to fix (400, 409, 410, 422 …), never for the statuses mapped here.
 */
export function friendlyApiMessage(status: number | null, opts: { serverMessage?: string | null; offline?: boolean } = {}): string {
  if (status === null || status === 0) return opts.offline ? FRIENDLY.offline : FRIENDLY.updating;
  if (status === 502 || status === 503 || status === 504) return FRIENDLY.updating;
  if (status === 413) return FRIENDLY.tooBig;
  if (status >= 500) return FRIENDLY.server;
  const msg = (opts.serverMessage ?? '').trim();
  // A specific, plain server sentence ("Only the author or a manager can do this", "Wrong PIN") says more than the
  // generic line, so it stays; bare reason phrases and framework defaults get the friendly line for their status.
  const specific = Boolean(msg) && looksHuman(msg) && !GENERIC_4XX.test(msg);
  if (status === 401) return specific ? msg : FRIENDLY.expired;
  if (status === 403) return specific ? msg : FRIENDLY.forbidden;
  if (status === 404) return specific ? msg : FRIENDLY.notFound;
  if (status === 429) return specific ? msg : FRIENDLY.tooMany;
  return msg && looksHuman(msg) ? msg : FRIENDLY.generic;
}

const GENERIC_4XX = /^(Unauthorized|Invalid access token|Missing bearer token|Forbidden( resource)?|Not Found|[\w ]{0,40} not found\.?|Cannot (GET|POST|PUT|PATCH|DELETE|HEAD) .*|Too many requests|Too Many Requests|Payload Too Large|Request Entity Too Large|Access denied)$/i;

/** A server message fit to show: not an HTML page, a stack trace or a bare reason phrase. */
function looksHuman(msg: string): boolean {
  if (msg.length > 400 || /<\/?[a-z][\s\S]*>/i.test(msg) || /\n\s+at\s|Error:\s|ECONN|ETIMEDOUT/i.test(msg)) return false;
  return !/^(Bad Request|Conflict|Unprocessable Entity|Gone|Precondition Failed|Request failed.*)$/i.test(msg);
}

/** A request that never got an HTTP answer (offline, DNS, connection dropped), after any retries. */
export class NetworkError extends Error {
  readonly status = 0;
  constructor(public readonly offline: boolean, public readonly attempts = 1, public readonly cause?: unknown) {
    super(offline ? FRIENDLY.offline : FRIENDLY.updating);
    this.name = 'NetworkError';
  }
}
